import { Box, Stack, styled, Typography } from '@mui/material';
import { StaticDateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { Loader, NamedLayoutBlock } from 'src/components';
import {
  GET_PLATFORM_USERS_QUERY,
  Partner,
  ServerPlatformUserRaw,
  useGroupRules,
  usePartner,
  usePlatformUser,
  usePlatformUsers,
  UserGroupRules,
  useSelectedPartner,
} from 'src/graphql';
import { useSignalOnCallScheduleEvents } from 'src/graphql/entities/signal-oncall-schedule/hooks/signal-oncall-schedule';
import {
  OncallManagementScheduleMutationRecord,
  OncallManagementScheduleRecord,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import { useManageUserNotificationPreferences } from 'src/graphql/entities/user-communication-preferences';
import { useGlobalStore } from 'src/store';
import { PlatformUserRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { useScreenDimensions } from 'src/utils/system';
import { SignalOnCallScheduleAgentsList } from './signal-oncall-schedule-agents-list';
import { SignalOnCallScheduleCalendar } from './signal-oncall-schedule-calendar';
import { SignalOnCallScheduleResponsiveLayout } from './signal-oncall-schedule-layout';
import { SignalOnCallScheduleNotificationEmailForm } from './signal-oncall-schedule-notification-email-form';

const CALENDAR_MAX_WIDTH = '18rem';

const TimezoneInfo = styled(Typography)(({ theme }) => ({
  padding: '1rem',
  paddingBottom: 0,
  color: theme.palette.custom.OV.sys.outline,
  fontSize: '0.8rem',
  fontWeight: 400,
}));

const StyledStaticDateTimePicker = styled(StaticDateTimePicker)(() => ({
  backgroundColor: 'transparent',
  minWidth: CALENDAR_MAX_WIDTH,
  '.MuiPickersCalendarHeader-root': {
    paddingLeft: '1rem',
  },
  '.MuiDateCalendar-root': {
    width: CALENDAR_MAX_WIDTH,
    paddingLeft: 0,
  },
  '.MuiPickersLayout-root': {
    paddingLeft: 0,
  },
  '.MuiMonthCalendar-root': {
    width: CALENDAR_MAX_WIDTH,
  },
  '.MuiYearCalendar-root': {
    width: CALENDAR_MAX_WIDTH,
  },
}));

const upsertScheduleRecords = (
  records: OncallManagementScheduleRecord[],
  record: OncallManagementScheduleRecord,
): OncallManagementScheduleRecord[] => {
  const index = records.findIndex(
    (it) => it.oncallManagementScheduleId === record.oncallManagementScheduleId,
  );

  if (index === -1) {
    return [...records, record];
  }

  const nextRecords = [...records];
  nextRecords[index] = { ...records[index], ...record };
  return nextRecords;
};

export const SignalOnCallScheduleView = ({
  partner,
  agents,
  events,
  hasLoadError,
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  onLoadMoreEvents,
}: {
  partner: Partner;
  agents: ServerPlatformUserRaw[];
  events: OncallManagementScheduleRecord[];
  hasLoadError?: boolean;
  onCreateEvent: (event: OncallManagementScheduleMutationRecord) => void;
  onUpdateEvent: (event: OncallManagementScheduleMutationRecord) => void;
  onDeleteEvent: (eventId: string) => void;
  onLoadMoreEvents: (selectedDate: Date) => void;
}): React.JSX.Element => {
  const { groupRules } = useGroupRules();
  const { isMobile } = useScreenDimensions();

  const { updatePartner } = usePartner();

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const { updatePlatformUser } = usePlatformUser({
    partnerId: partner.id,
  });

  const { manageUserCommunicationPreferences } = useManageUserNotificationPreferences();

  const onUserUpdate = useCallback(
    async (data: Partial<PlatformUserRaw>) => {
      try {
        await updatePlatformUser(data);
        await manageUserCommunicationPreferences({
          userId: data.id!,
          userCommunicationPreferences: data.userCommunicationPreferences || [],
          userGroups: data.userGroups || [],
        });
      } catch {
        showErrorAlert('Failed to update user');
      } finally {
        showSuccessAlert('User updated successfully');
      }
    },
    [manageUserCommunicationPreferences, showErrorAlert, showSuccessAlert, updatePlatformUser],
  );

  const isAdmin = useMemo(
    () => !!(groupRules[UserGroupRules.Admin] || groupRules[UserGroupRules.SuperAdmin]),
    [groupRules],
  );

  // `null` is provided as a possible type to match the type of `StaticDateTimePicker`'s `value` prop.
  const [currentDisplayedDate, setCurrentDisplayedDate] = useState<Date>(new Date());

  useEffect(() => {
    onLoadMoreEvents(currentDisplayedDate);
  }, [currentDisplayedDate, onLoadMoreEvents]);

  const onChangeNotificationEmail = useCallback(
    (email?: string) => {
      return updatePartner(partner.partnerId, { globalNotificationEmail: email });
    },
    [partner.partnerId, updatePartner],
  );

  return (
    <SignalOnCallScheduleResponsiveLayout
      isMobile={isMobile}
      sidebar={
        <>
          {partner?.timeZone ? <TimezoneInfo>{partner.timeZone}</TimezoneInfo> : null}
          <Box maxWidth={CALENDAR_MAX_WIDTH}>
            <StyledStaticDateTimePicker
              displayStaticWrapperAs="desktop"
              openTo="day"
              value={dayjs(currentDisplayedDate)}
              views={['year', 'month', 'day']}
              onChange={(date) => {
                if (date) {
                  setCurrentDisplayedDate(date?.toDate() || null);
                }
              }}
            />
          </Box>

          <SignalOnCallScheduleNotificationEmailForm
            disabled={!isAdmin}
            partnerId={partner.partnerId}
            email={partner.globalNotificationEmail}
            onSubmit={onChangeNotificationEmail}
          />

          <SignalOnCallScheduleAgentsList agents={agents} onUserUpdate={onUserUpdate} />
        </>
      }
      calendar={
        <SignalOnCallScheduleCalendar
          partnerId={partner.partnerId}
          selectedDate={currentDisplayedDate}
          onDateChange={setCurrentDisplayedDate}
          isMobile={isMobile}
          agents={agents}
          events={events}
          timezone={partner.timeZone}
          canManageSchedules={isAdmin}
          hasLoadError={hasLoadError}
          onCreateEvent={onCreateEvent}
          onUpdateEvent={onUpdateEvent}
          onDeleteEvent={onDeleteEvent}
        />
      }
    />
  );
};

export const SignalOnCallSchedule = (): React.JSX.Element => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const {
    records: events,
    isLoaded: isEventsLoaded,
    cacheManager,
    loadMore,
    error: eventsError,
  } = useSignalOnCallScheduleEvents(selectedPartnerId);

  const {
    platformUsers: signalAgents,
    isLoaded: isLoadedSignalAgents,
    error: agentsError,
  } = usePlatformUsers(
    {
      partnerId: selectedPartnerId,
      shouldIncludePlatformPartnerId: false,
      removeNestedFilters: false,
      filters: [
        {
          attribute: 'userGroups/ruleValues/userGroupRuleId',
          operator: '=',
          value: UserGroupRules.ProvisionSignal,
        },
        {
          attribute: 'isActive',
          operator: '=',
          value: true,
        },
      ],
      query: GET_PLATFORM_USERS_QUERY,
    },
    {
      skip: isNullish(selectedPartnerId),
    },
  );

  const { selectedPartner: partner } = useSelectedPartner();

  // Agents previously had no error escape — a failed getPlatformUser left isLoaded false forever
  // and the page spun. Treat query errors as "ready" the same way events already do.
  const isLoading =
    !partner || (!isLoadedSignalAgents && !agentsError) || (!isEventsLoaded && !eventsError);

  const onCreateEvent = useCallback(
    (event: OncallManagementScheduleMutationRecord) => {
      cacheManager.update((cache) => {
        if (!cache.getOncallManagementSchedule?.records) {
          return;
        }

        const primaryAgent = signalAgents.find((it) => it.id === event.primaryAgentId);
        const secondaryAgent = event.secondaryAgentId
          ? (signalAgents.find((it) => it.id === event.secondaryAgentId) ?? null)
          : null;

        if (partner && primaryAgent) {
          const nextRecord: OncallManagementScheduleRecord = {
            ...event,
            partner,
            primaryAgent,
            secondaryAgent,
          };
          cache.getOncallManagementSchedule.records = upsertScheduleRecords(
            cache.getOncallManagementSchedule.records,
            nextRecord,
          );
        }
      });
    },
    [cacheManager, partner, signalAgents],
  );

  const onUpdateEvent = useCallback(
    (event: OncallManagementScheduleMutationRecord) => {
      cacheManager.update((cache) => {
        if (!cache.getOncallManagementSchedule?.records) {
          return;
        }

        const primaryAgent = signalAgents.find((it) => it.id === event.primaryAgentId);
        const secondaryAgent = event.secondaryAgentId
          ? (signalAgents.find((it) => it.id === event.secondaryAgentId) ?? null)
          : null;

        if (partner && primaryAgent) {
          const nextRecord: OncallManagementScheduleRecord = {
            ...event,
            partner,
            primaryAgent,
            secondaryAgent,
          };
          cache.getOncallManagementSchedule.records = upsertScheduleRecords(
            cache.getOncallManagementSchedule.records,
            nextRecord,
          );
        }
      });
    },
    [cacheManager, partner, signalAgents],
  );

  const onDeleteEvent = useCallback(
    (eventId: string) => {
      cacheManager.update((cache) => {
        if (!cache.getOncallManagementSchedule?.records) {
          return;
        }

        cache.getOncallManagementSchedule.records =
          cache.getOncallManagementSchedule.records.filter((it) => it.id !== eventId);
        cacheManager.client.cache.evict({ id: eventId });
        cacheManager.client.cache.gc();
      });
    },
    [cacheManager],
  );

  return (
    <NamedLayoutBlock
      title="Signal"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Stack spacing={2} sx={{ overflow: 'hidden', height: '100vh' }}>
        {isLoading ? (
          <Loader />
        ) : (
          <SignalOnCallScheduleView
            partner={partner}
            agents={signalAgents}
            events={events}
            hasLoadError={Boolean(eventsError)}
            onCreateEvent={onCreateEvent}
            onUpdateEvent={onUpdateEvent}
            onDeleteEvent={onDeleteEvent}
            onLoadMoreEvents={loadMore}
          />
        )}
      </Stack>
    </NamedLayoutBlock>
  );
};
